/**
 * Export and import (spec §10, §11). The browser download and file picker belong to the UI (M2-18); this module
 * turns a slot into a named JSON text and a text back into a stored, validated and migrated save.
 *
 * - Export works for any non-empty slot. A valid save is re-indented as it is stored (never migrated or
 *   re-serialised); a corrupt or unmigratable one is exported as its raw text, so the player keeps it.
 * - Import validates and migrates first; on failure nothing is written and the error names the Zod path.
 * - D-337 (save export carries the Reach): the strategic save stored beside the record (the companion, keyed by
 *   the record) is bundled as the top-level field `strategicCompanion: { version, savedAt, save }`. Import checks it
 *   with Zod, strips it before the record is migrated, and stores it under the imported record's key, so the
 *   Reach (world flags, Charter votes, claims, gate stability, the corporation) loads with the career. A file
 *   without it (an older export) imports as before: a fresh Reach, with a warning in the log.
 */
import { fail, ok, type CmdResult } from '../career/result'
import { logger } from '../core/logger'
import type { ManualSlotId, SaveExport, SaveSlotId, SaveSlotInfo } from '../ui/contracts'
import { migrate } from './migrations'
import type { SaveManualOptions, SaveSlots } from './slots'
import type { CompanionStore } from './storage'
import {
  EXPORT_COMPANION_FIELD,
  EXPORT_COMPANION_VERSION,
  encodeEnvelope,
  envelopeSchema,
  exportCompanionSchema,
  recordKeyOf,
  recordKeyOfValue,
  type ExportCompanion,
} from './strategicCompanion'

/** `umbra-reach-<slot>-<YYYY-MM-DD>.json` (the local date of `now`). */
export function exportFileName(slot: SaveSlotId, now: Date = new Date()): string {
  const y = String(now.getFullYear())
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `umbra-reach-${slot}-${y}-${m}-${d}.json`
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** The companion stored for a record (parsed JSON), as its export field, or null when there is none. */
async function companionFor(store: CompanionStore | undefined, record: unknown): Promise<ExportCompanion | null> {
  const rec = recordKeyOfValue(record)
  if (store === undefined || rec === null) return null
  let text: string | null
  try {
    text = (await store.get(rec.key)) ?? (await store.get(rec.legacyKey))
  } catch (err) {
    logger.warn('export: reading the stored Reach failed; exporting the career alone', err)
    return null
  }
  if (text === null) return null
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    logger.warn('export: the stored Reach is not JSON; exporting the career alone')
    return null
  }
  const env = envelopeSchema.safeParse(raw)
  if (!env.success) {
    logger.warn('export: the stored Reach is malformed; exporting the career alone', env.error.issues)
    return null
  }
  return { version: EXPORT_COMPANION_VERSION, savedAt: env.data.savedAt, save: env.data.save }
}

/**
 * Pretty JSON of the stored text plus its strategic companion (`strategicCompanion`, when one is stored), or the
 * raw text when it is not JSON. Null for an empty slot.
 */
export async function exportSlot(slots: SaveSlots, slot: SaveSlotId, now: Date = new Date()): Promise<SaveExport | null> {
  const text = await slots.rawText(slot)
  if (text === null) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    // Not JSON: export the bytes as stored.
    return { fileName: exportFileName(slot, now), text }
  }
  const pretty = JSON.stringify(parsed, null, 2)
  const companion = isPlainObject(parsed) ? await companionFor(slots.storage.companion, parsed) : null
  if (companion === null) return { fileName: exportFileName(slot, now), text: pretty }
  // The record stays indented; the companion (about 100-400 KB, D-337) is written compact as the last field, which
  // keeps the file about a sixth smaller than indenting it too. A companion only exists for a record, so the
  // object is not empty.
  const body = pretty.slice(0, pretty.lastIndexOf('}')).trimEnd()
  return { fileName: exportFileName(slot, now), text: `${body},
  "${EXPORT_COMPANION_FIELD}": ${JSON.stringify(companion)}
}` }
}

export interface ImportOptions extends SaveManualOptions {
  /** Migration options (tests inject a chain). Default: the app chain (`migrations`). */
  migrate?: Parameters<typeof migrate>[1]
}

/**
 * Parses, migrates and validates `text`, then stores the (current-version) record in `slot`. Errors:
 * `invalidImport` (not JSON, or fails validation: the message names the Zod path), `future`,
 * `migrationFailed`, and the slot's own refusals (`corrupt` target without `overwriteBad`, `storageError`).
 */
export async function importSave(
  slots: SaveSlots,
  text: string,
  slot: ManualSlotId,
  options: ImportOptions = {},
): Promise<CmdResult<SaveSlotInfo>> {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch (err) {
    return fail('invalidImport', `Import failed: not a JSON file (${err instanceof Error ? err.message : String(err)}).`)
  }
  let companion: ExportCompanion | null = null
  if (isPlainObject(raw) && EXPORT_COMPANION_FIELD in raw) {
    const { [EXPORT_COMPANION_FIELD]: bundled, ...rest } = raw
    const c = exportCompanionSchema.safeParse(bundled)
    if (!c.success) {
      const issue = c.error.issues[0]
      const path = [EXPORT_COMPANION_FIELD, ...(issue?.path ?? []).map(String)].join('.')
      return fail('invalidImport', `Import failed: Invalid save: ${path}: ${issue?.message ?? 'invalid'}`, { path })
    }
    companion = c.data
    raw = rest
  } else if (isPlainObject(raw)) {
    logger.warn('import: the file has no strategic companion (an older export); the Reach starts fresh for it')
  }
  const migrated = migrate(raw, options.migrate)
  if (!migrated.ok) {
    const { code, message, detail } = migrated.error
    return code === 'corrupt' ? fail('invalidImport', `Import failed: ${message}`, detail) : fail(code, `Import failed: ${message}`, detail)
  }
  const saved = await slots.saveManual(slot, migrated.value.record, { overwriteBad: options.overwriteBad })
  if (!saved.ok) return saved
  if (companion !== null) await storeCompanion(slots, slot, companion)
  return ok(saved.value)
}

/**
 * Stores an imported companion under the stored record's key, rewritten with the slot text in one transaction
 * (the slot then points at the key, so the bridge's cleanup keeps it). A failure keeps the imported career and
 * logs a warning: the Reach then starts fresh, as it did before the companion was bundled.
 */
async function storeCompanion(slots: SaveSlots, slot: ManualSlotId, companion: ExportCompanion): Promise<void> {
  const store = slots.storage.companion
  try {
    const text = await slots.rawText(slot)
    const rec = text === null ? null : recordKeyOf(text)
    if (store === undefined || text === null || rec === null) {
      logger.warn('import: no companion store here; the imported career starts a fresh Reach')
      return
    }
    await store.putWith(slot, text, rec.key, encodeEnvelope({ savedAt: companion.savedAt, save: companion.save }))
  } catch (err) {
    logger.warn('import: storing the imported Reach failed; the imported career starts a fresh Reach', err)
  }
}
